import { Inject, Injectable } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { UrlKey, UrlService } from "../../url/url.service";
import { OktaAuth } from "@okta/okta-auth-js";
import { OKTA_AUTH } from "@okta/okta-angular";
import { Observable, from, switchMap, firstValueFrom } from "rxjs";
import { IHttpService } from "../IHttpService";
import { WebLogger } from "../../../web-logger";
import { ENVIRONMENT_CONFIG, EnvironmentConfig } from "../../../environment.config";

@Injectable({
  providedIn: "root",
})
export class HttpOktaService implements IHttpService {
  // Cookie entries
  protected readonly POID = "POID"
  protected readonly XSRF_TOKEN = "XSRF-TOKEN"
  protected readonly X_XSRF_TOKEN = "X-XSRF-TOKEN"
  protected readonly ALLOW_ORIGIN_HEADER: string = "Access-Control-Allow-Origin"
  protected readonly AUTHORIZATION_HEADER: string = "Authorization"
  //Whitespace is intentional
  protected readonly BEARER_HEADER: string = "Bearer "
  protected readonly CONSUMER_HEADER: string = "Consumer-Application-Code";
  protected readonly CONSUMER_VALUE: string = "ROB";

  constructor(
    @Inject(OKTA_AUTH) public oktaAuth: OktaAuth,
    @Inject(ENVIRONMENT_CONFIG) private readonly config: EnvironmentConfig,
    private readonly httpClient: HttpClient,
    private readonly urlService: UrlService,
  ) {}

  doPostRequest<Type>(urlKey: UrlKey, data: any, additionalHeaders: object = {}, urlParameters?: object): Observable<any> {
    // Ensure XSRF token exists before making POST request
    return from(this.ensureXsrfToken()).pipe(
      switchMap(() => {
        //get Okta JWT
        const accessToken = this.oktaAuth.getAccessToken()
        const url = this.urlService.getUrl(urlKey, urlParameters)
        const headers = new HttpHeaders()
          .set(this.ALLOW_ORIGIN_HEADER, this.config.appUrl || '')
          .set(this.AUTHORIZATION_HEADER, this.BEARER_HEADER + accessToken)
          .set(this.X_XSRF_TOKEN, this.getCookie(this.XSRF_TOKEN))
          .set(this.CONSUMER_HEADER, this.CONSUMER_VALUE)
          .set("Access-Control-Allow-Credentials", "true") //TODO: REMOVE THIS FOR THE LOVE OF GOD
        this.applyAdditionalHeaders(headers, additionalHeaders)

        return this.httpClient.post<Type>(url, JSON.stringify(data), {
          headers: headers,
          withCredentials: true
        });
      })
    );
  }

  doPutRequest<Type>(urlKey: UrlKey, data: any, additionalHeaders: object = {}, urlParameters?: object): Observable<any> {
    // Ensure XSRF token exists before making PUT request
    return from(this.ensureXsrfToken()).pipe(
      switchMap(() => {
        //get Okta JWT
        const accessToken = this.oktaAuth.getAccessToken()
        const url = this.urlService.getUrl(urlKey, urlParameters)
        const headers = new HttpHeaders()
          .set(this.ALLOW_ORIGIN_HEADER, this.config.appUrl || '')
          .set(this.AUTHORIZATION_HEADER, this.BEARER_HEADER + accessToken)
          .set(this.X_XSRF_TOKEN, this.getCookie(this.XSRF_TOKEN))
          .set(this.CONSUMER_HEADER, this.CONSUMER_VALUE)
          .set("Access-Control-Allow-Credentials", "true") //TODO: REMOVE THIS FOR THE LOVE OF GOD
        this.applyAdditionalHeaders(headers, additionalHeaders)

        return this.httpClient.put<Type>(url, JSON.stringify(data), {
          headers: headers,
          withCredentials: true
        });
      })
    );
  }

  doGetRequest<Type>(urlKey: UrlKey, additionalHeaders: object = {}, urlParameters?: object): Observable<any> {
    //get Okta JWT
    const accessToken = this.oktaAuth.getAccessToken()
    const url = this.urlService.getUrl(urlKey, urlParameters)

    const headers = new HttpHeaders()
      .set(this.ALLOW_ORIGIN_HEADER, this.config.appUrl || '')
      .set(this.AUTHORIZATION_HEADER, this.BEARER_HEADER + accessToken)
      .set(this.CONSUMER_HEADER, this.CONSUMER_VALUE)
      .set("Access-Control-Allow-Credentials", "true") //TODO: REMOVE THIS FOR THE LOVE OF GOD
    this.applyAdditionalHeaders(headers, additionalHeaders)

    return this.httpClient.get<Type>(url, {
      headers: headers,
      withCredentials: true
    });
  }

  /**
   * Ensures XSRF-TOKEN cookie exists. If not, makes a call to WAH GET status
   * to generate the token before proceeding with POST requests.
   */
  private async ensureXsrfToken(): Promise<void> {
    // If the XSRF-TOKEN cookie already exists, no need to generate it again
    if (this.hasCookie(this.XSRF_TOKEN)) {
      return;
    }

    try {
      const accessToken = this.oktaAuth.getAccessToken();

      if (!accessToken) {
        WebLogger.log({
          message: "No Okta access token available, skipping XSRF token generation",
          level: "warn"
        });
        return;
      }

      // Make authenticated GET request to WAH status endpoint to generate XSRF-TOKEN cookie
      await firstValueFrom(
        // after! is the non-null assertion operator, where we can guarantee the value won't be undefined/null at runtime
        this.httpClient.get(this.config.xsrfGetCall!, {
          withCredentials: true,
          headers: {
            'Authorization': `Bearer ${accessToken}`
          }
        })
      );

      WebLogger.log({ message: "XSRF token generated successfully", level: "info" });
    } catch (error) {
      WebLogger.log({
        message: "Failed to generate XSRF token",
        level: "error",
        payload: { error }
      });
    }
  }

  hasCookie(name: string): boolean {
    const cookies = document.cookie.split(";");
    return cookies.some((cookie) => cookie.split("=")[0].replace(" ", "") === name);
  }

  getCookie(name: string): any {
    const cookies = document.cookie.split(";");
    const cookie = cookies.find((cookie) => cookie.split("=")[0].replace(" ", "") === name);
    if (cookie) {
      return cookie.split("=")[1];
    }
    WebLogger.log({ message: "HttpOkta cookie not found", level: "error", payload: { cookieName: name } });
    throw new Error(`No ${name} cookie found`);
  }

  doDeleteRequest<Type>(urlKey: UrlKey, additionalHeaders: object = {}, urlParameters?: object): Observable<any> {
    return from(this.ensureXsrfToken()).pipe(
      switchMap(() => {
        const accessToken = this.oktaAuth.getAccessToken()
        const url = this.urlService.getUrl(urlKey, urlParameters)
        const headers = new HttpHeaders()
          .set(this.ALLOW_ORIGIN_HEADER, this.config.appUrl || '')
          .set(this.AUTHORIZATION_HEADER, this.BEARER_HEADER + accessToken)
          .set(this.X_XSRF_TOKEN, this.getCookie(this.XSRF_TOKEN))
          .set(this.CONSUMER_HEADER, this.CONSUMER_VALUE)
          .set("Access-Control-Allow-Credentials", "true")
        this.applyAdditionalHeaders(headers, additionalHeaders)

        return this.httpClient.delete<Type>(url, {
          headers: headers,
          withCredentials: true
        });
      })
    );
  }

  applyAdditionalHeaders(headers: HttpHeaders, additionalHeaders: Object): any {
    if (additionalHeaders) {
      Object.keys(additionalHeaders).forEach((key) => {
        headers = headers.set(key, additionalHeaders[key]);
      });
    }
  }
  doSimpleGetRequest<Type>(urlKey: UrlKey, urlParameters?: object): Observable<any> {
    const url = this.urlService.getUrl(urlKey, urlParameters);
    return this.httpClient.get<Type>(url);
  }
}
